/**
 * Connector matrix + per-connector recent timeline (Phase A.3).
 *
 * - Polls `GET /connectors/:chargerId` at 60s while active.
 * - Tap a connector card to inspect its recent transitions via
 *   `GET /connectors/:chargerId/timeline?evseId=…&connectorId=…`.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import OcppEmptyState from '../components/OcppEmptyState';
import OcppErrorBanner from '../components/OcppErrorBanner';
import OcppKvList from '../components/OcppKvList';
import OcppLoadingState from '../components/OcppLoadingState';
import OcppSectionCard from '../components/OcppSectionCard';
import OcppStatusPill from '../components/OcppStatusPill';
import { OcppTheme } from '../components/OcppTheme';
import { fetchConnectorMatrix, fetchConnectorTimeline } from '../services';
import { isOcppSuccess, mapOcppApiError, type OcppApiErrorInfo } from '../utils/apiErrors';
import {
  connectorStatusColor,
  connectorStatusLabel,
  formatOcppTimestamp,
} from '../utils/statusFormat';
import { usePolledResource } from '../hooks/usePolledResource';
import type {
  ConnectorState,
  ConnectorTransition,
  GetConnectorMatrixResponse,
  GetConnectorTimelineResponse,
} from '../models';
import type { OcppTabProps } from './types';

const POLL_INTERVAL_MS = 60_000;

const ConnectorsTab: React.FC<OcppTabProps> = ({ chargerId, isActive }) => {
  const fetcher = useCallback(() => fetchConnectorMatrix(chargerId), [chargerId]);
  const { data, error, loading, refreshing, reload } = usePolledResource<GetConnectorMatrixResponse>(
    fetcher,
    { intervalMs: POLL_INTERVAL_MS, enabled: isActive }
  );

  const [selected, setSelected] = useState<ConnectorState | null>(null);

  if (loading) return <OcppLoadingState label="Loading connectors…" />;
  if (error) return <OcppErrorBanner error={error} onRetry={reload} />;

  const connectors = data?.connectors ?? [];
  if (connectors.length === 0) {
    return (
      <OcppEmptyState
        icon="power"
        title="No connectors reported"
        description="The charger has not yet sent its connector matrix. Try refreshing."
      />
    );
  }

  return (
    <View>
      {refreshing ? <Text style={styles.refreshHint}>Refreshing…</Text> : null}
      {connectors.map((c) => (
        <ConnectorCard key={c.id} connector={c} onPress={() => setSelected(c)} />
      ))}

      <Modal
        visible={selected !== null}
        animationType="slide"
        onRequestClose={() => setSelected(null)}
      >
        {selected ? (
          <ConnectorTimelineModal
            chargerId={chargerId}
            connector={selected}
            onClose={() => setSelected(null)}
          />
        ) : null}
      </Modal>
    </View>
  );
};

export default ConnectorsTab;

const ConnectorCard: React.FC<{ connector: ConnectorState; onPress: () => void }> = ({
  connector,
  onPress,
}) => {
  const color = connectorStatusColor(connector.status);
  return (
    <OcppSectionCard
      title={`EVSE ${connector.evse_id} · Connector ${connector.connector_id}`}
      subtitle={`Last update ${formatOcppTimestamp(connector.last_status_update)}`}
      trailing={<OcppStatusPill label={connectorStatusLabel(connector.status)} color={color} />}
    >
      <OcppKvList
        items={[
          { key: 'Vehicle connected', value: connector.vehicle_connected ? 'Yes' : 'No' },
          { key: 'Charging', value: connector.vehicle_charging ? 'Yes' : 'No' },
          { key: 'Active tx', value: connector.active_transaction_id ?? '—' },
          { key: 'Error', value: connector.error_code ?? '—' },
          { key: 'Vendor error', value: connector.vendor_error_code ?? '—' },
          { key: 'Info', value: connector.info ?? '—' },
        ]}
      />
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        style={({ pressed }) => [styles.timelineLink, pressed && { opacity: 0.7 }]}
      >
        <MaterialIcons name="history" size={16} color={OcppTheme.accent} />
        <Text style={styles.timelineLinkText}>View status timeline</Text>
      </Pressable>
    </OcppSectionCard>
  );
};

const ConnectorTimelineModal: React.FC<{
  chargerId: string;
  connector: ConnectorState;
  onClose: () => void;
}> = ({ chargerId, connector, onClose }) => {
  const [rows, setRows] = useState<ConnectorTransition[]>([]);
  const [err, setErr] = useState<OcppApiErrorInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchConnectorTimeline(chargerId, {
      evseId: connector.evse_id,
      connectorId: connector.connector_id,
      limit: 100,
    });
    if (isOcppSuccess(res.status)) {
      setRows(((res.payload as GetConnectorTimelineResponse | null)?.transitions ?? []));
      setErr(null);
    } else {
      setErr(mapOcppApiError(res.status, res.payload));
    }
    setLoading(false);
  }, [chargerId, connector.evse_id, connector.connector_id]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const title = useMemo(
    () => `EVSE ${connector.evse_id} · Connector ${connector.connector_id} timeline`,
    [connector]
  );

  return (
    <SafeAreaView style={styles.modalSafe}>
      <View style={styles.modalHeader}>
        <Text style={styles.modalTitle}>{title}</Text>
        <Pressable
          hitSlop={12}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close timeline"
        >
          <MaterialIcons name="close" size={26} color="#E5E7EB" />
        </Pressable>
      </View>
      <ScrollView contentContainerStyle={{ padding: 14 }}>
        {loading ? (
          <OcppLoadingState label="Loading timeline…" />
        ) : err ? (
          <OcppErrorBanner error={err} onRetry={load} />
        ) : rows.length === 0 ? (
          <OcppEmptyState icon="schedule" title="No transitions" />
        ) : (
          rows.map((row) => (
            <View key={row.id} style={styles.transitionRow}>
              <View
                style={[styles.dot, { backgroundColor: connectorStatusColor(row.to_status) }]}
              />
              <View style={{ flex: 1 }}>
                <Text style={styles.transitionTitle}>
                  {row.from_status ?? '—'} → {row.to_status}
                </Text>
                <Text style={styles.transitionMeta}>
                  {formatOcppTimestamp(row.occurred_at)}
                  {row.error_code ? ` · ${row.error_code}` : ''}
                  {row.transaction_id ? ` · tx ${row.transaction_id}` : ''}
                </Text>
              </View>
            </View>
          ))
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  refreshHint: {
    color: OcppTheme.textMuted,
    fontSize: 12,
    marginBottom: 8,
  },
  timelineLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
  },
  timelineLinkText: {
    color: OcppTheme.accent,
    fontSize: 13,
    fontWeight: '700',
  },
  modalSafe: {
    flex: 1,
    backgroundColor: '#111418',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: OcppTheme.border,
  },
  modalTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '700',
    color: OcppTheme.textPrimary,
    marginRight: 12,
  },
  transitionRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    gap: 10,
    alignItems: 'flex-start',
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginTop: 4,
  },
  transitionTitle: {
    color: OcppTheme.textPrimary,
    fontSize: 14,
    fontWeight: '600',
  },
  transitionMeta: {
    color: OcppTheme.textMuted,
    fontSize: 12,
    marginTop: 2,
  },
});
